import HelpError from 'components/Form/HelpError';
import {RequiredType} from 'components/Form/types';
import {TooltipWrapperProps} from 'components/Tooltip/types';
import useLayout from 'hooks/useLayout';
import {FC, isValidElement, ReactNode, useEffect, useRef} from 'react';
import {FieldError} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {WithRequiredProperty} from 'types/utils';

import MaxLength from '../MaxLength';
import FieldLabel from './FieldLabel';

export type FieldProps = {
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    error?: FieldError;
    helpText?: ReactNode;
    isCompact?: boolean;
    label?: ReactNode;
    markOptional?: boolean;
    required?: boolean;
    requiredType?: RequiredType;
    scrollIntoViewOnError?: boolean;
    tooltip?: WithRequiredProperty<TooltipWrapperProps, 'message'>;
} & (WithLabel | WithMaxLength | WithoutLabel | WithValue);

type WithLabel = {
    maxLength?: never;
    name: string;
    type: 'select';
};

type WithMaxLength = {
    maxLength?: number;
    name: string;
    type: 'checkbox' | 'input' | 'password' | 'textarea';
};

type WithoutLabel = {
    maxLength?: never;
    name?: never;
    type: 'button' | 'checkbox' | 'divider' | 'radio';
};

type WithValue = {
    maxLength?: never;
    name?: never;
    type: 'value';
};

const CENTER = {
    button: true,
    checkbox: false,
    divider: false,
    input: true,
    password: false,
    radio: false,
    select: true,
    textarea: false,
    value: false,
};

const _reactNodeToString = (node: ReactNode | ReactNode[]): string => {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (isValidElement(node) && node.props.children) {
        return _reactNodeToString(node.props.children);
    }
    if (Array.isArray(node)) {
        return node.map(_reactNodeToString).join('');
    }
    return '';
};

const Field: FC<FieldProps> = ({
    children,
    className,
    disabled,
    error,
    helpText,
    isCompact = false,
    label,
    markOptional,
    maxLength,
    name,
    required,
    requiredType = 'text',
    scrollIntoViewOnError,
    tooltip,
    type,
}) => {
    const {classNameLabel, layout} = useLayout();

    const sideBySide = layout === 'sideBySide';

    const helpError =
        helpText || error || maxLength ? (
            <HelpError
                disabled={disabled}
                error={error}
                helpText={helpText}
                label={label}
            >
                {maxLength && <MaxLength maxLength={maxLength} name={name} />}
            </HelpError>
        ) : null;

    const fieldRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (scrollIntoViewOnError && error) {
            fieldRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [fieldRef, helpError]);

    const fieldLabel = label ? (
        <FieldLabel
            className={twMerge(
                type === 'value' && sideBySide && 'text-right',
                !sideBySide && 'font-semibold',
            )}
            disabled={disabled}
            error={error}
            htmlFor={name}
            isSideBySide={sideBySide}
            markOptional={markOptional}
            required={required}
            requiredType={requiredType}
            tooltip={tooltip}
        >
            {label}
        </FieldLabel>
    ) : null;

    if (!sideBySide) {
        return (
            <div className={className} ref={fieldRef} role="presentation">
                {fieldLabel}
                {children}
                {helpError}
            </div>
        );
    }

    return (
        <>
            {fieldLabel ? (
                <div
                    aria-label={
                        label ? `${_reactNodeToString(label)}-label` : undefined
                    }
                    className={twMerge(
                        'self-start',
                        CENTER[type] && 'flex justify-end items-center w-full',
                        CENTER[type] && isCompact && 'min-h-8',
                        CENTER[type] && !isCompact && 'min-h-10',
                        type === 'password' && !required && 'pt-2.5',
                        classNameLabel,
                    )}
                    data-field-required={!!required}
                    data-field-type={type}
                    role="gridcell"
                >
                    {fieldLabel}
                </div>
            ) : (
                <div />
            )}
            <div
                aria-label={
                    label ? `${_reactNodeToString(label)}-value` : undefined
                }
                className={twMerge('flex', type !== 'button' && 'flex-col')}
                data-field-type={type}
                ref={fieldRef}
                role="gridcell"
            >
                {children}
                {helpError}
            </div>
        </>
    );
};

export default Field;
